import {
  Box,
  Button,
  ButtonLink,
  IconButton,
  IconClose,
  NXDPortal,
  Stack,
} from '@n26/nxd'
import * as Dialog from '@radix-ui/react-dialog'
import QRCode from 'qrcode'
import { useState, useEffect } from 'react'
import { useLocaliseString } from '@hooks/useLocaliseString'
import type { SignupButtonProps } from '@components/SignupButton'
import { logger } from '@helpers/core/logger'
import {
  addTrackingParamsToUrl,
  getTrackingParams,
} from '@helpers/getTrackingParams'

type QrCodeCtaProps = Pick<
  SignupButtonProps,
  'qrCodeCta' | 'variant' | 'size'
> & {
  pageHref: string
  onClick?: (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLAnchorElement>
  ) => void
}

type AllTrackingParams = ReturnType<typeof getTrackingParams> & {
  ch?: string
  pr?: string
}

const Line = () => (
  <Box
    borderTopWidth='1'
    borderColor='neutral-alternate'
    width='full'
    className='my-[13px]'
  />
)

export const QrCodeCta = ({
  qrCodeCta,
  pageHref,
  size,
  variant,
  onClick,
}: QrCodeCtaProps) => {
  const {
    heading,
    subheading,
    instructions,
    secondarySubheading,
    ctaLabelApp,
    ctaLabelWeb,
    showCta,
    appDownloadData,
  } = qrCodeCta || {}

  const localiseString = useLocaliseString()

  const [qrCode, setQrCode] = useState<string | undefined>(undefined)
  const [
    appDownloadUrlWithTrackingParams,
    setAppDownloadUrlWithTrackingParams,
  ] = useState<string | undefined>(undefined)

  const generateQRCode = async (data: string) => {
    try {
      const response = await QRCode.toDataURL(data)
      setQrCode(response)
    } catch (error) {
      logger.error('QR_CODE_GENERATION_ERROR', error)
    }
  }

  const { websiteUrl, appDownloadUrl, requestUrl, ch, pr } =
    appDownloadData || {}

  useEffect(() => {
    if (websiteUrl && appDownloadUrl && requestUrl) {
      const qrCodeUrl = websiteUrl + '/qr-code'

      const trackingParams = getTrackingParams(
        document.cookie,
        new URL(requestUrl).searchParams
      )

      // only add ch and pr to the tracking params if they are non-empty
      const allTrackingParams: AllTrackingParams = { ...trackingParams }

      if (ch) {
        allTrackingParams.ch = ch
      }

      if (pr) {
        allTrackingParams.pr = pr
      }

      const urlWithParams = addTrackingParamsToUrl(appDownloadUrl, {
        ...allTrackingParams,
      })

      const qrCodeUrlWithParams = addTrackingParamsToUrl(qrCodeUrl, {
        ...allTrackingParams,
      })

      // The QR code links to an internal URl before being redirected so that
      // we can track the scan
      generateQRCode(qrCodeUrlWithParams)
      setAppDownloadUrlWithTrackingParams(urlWithParams)
    }
  }, [websiteUrl, appDownloadUrl, requestUrl, ch, pr])

  const popUpDimensions = showCta ? 'h-fit w-[584px]' : 'h-fit w-[488px]'
  const qrCodeDimensions = showCta
    ? 'h-[180px] w-[180px]'
    : 'h-[160px] w-[160px]'

  return (
    <>
      <Box display={{ mobile: 'none', desktop: 'block' }}>
        <Dialog.Root>
          <Dialog.Trigger asChild>
            <Button size={size} variant={variant} onClick={onClick}>
              {ctaLabelApp || ''}
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <NXDPortal>
              <Dialog.Overlay className='z-modal bg-[rgba(0,0,0,0.4)] data-[state=open]:animate-overlayShow fixed inset-[0]' />
              <Dialog.Content
                className={`z-modal data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] ${popUpDimensions} translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-neutral px-24 py-[56px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none content-center justify-center text-center items-center`}
              >
                <Stack>
                  <Dialog.Title asChild>
                    <Stack align='center' justify='center'>
                      {/* We need to use non-responsive styling here, hence the custom heading */}
                      <Box
                        as='h2'
                        className='font-extended text-36 leading-[44px] lg:text-40 lg:leading-[48px]'
                      >
                        {heading}
                      </Box>
                    </Stack>
                  </Dialog.Title>
                  <Dialog.Description asChild>
                    <Box
                      textAlign='center'
                      alignItems='center'
                      display='flex'
                      flexDirection='column'
                      className={
                        showCta ? 'gap-16 pt-16' : 'gap-[28px] pt-[28px]'
                      }
                    >
                      {/* We need to use non-responsive styling here, hence the custom heading */}
                      {subheading && showCta && (
                        <Box
                          as='h3'
                          className='text-24 leading-8 lg:text-28 lg:leading-9'
                        >
                          {subheading}
                        </Box>
                      )}
                      <Box className={qrCodeDimensions}>
                        {qrCode && <img src={qrCode} alt='QR Code' />}
                      </Box>
                      {instructions && (
                        <Box as='p' className='text-20 leading-8'>
                          {instructions}
                        </Box>
                      )}
                      {showCta && (
                        <>
                          <Box
                            display='flex'
                            flexDirection='row'
                            color='neutral-alternate'
                            className='w-full max-w-[440px]'
                            gap='8'
                            justifyContent='center'
                          >
                            <Line />
                            <p className='text-16 leading-6 text-neutral-alternate'>
                              {localiseString('qrCodeSeparation')}
                            </p>
                            <Line />
                          </Box>
                          <p className='text-24 leading-8 lg:text-28 lg:leading-9'>
                            {secondarySubheading}
                          </p>
                        </>
                      )}
                    </Box>
                  </Dialog.Description>
                  {showCta && (
                    <Stack justify='center' align='center'>
                      <Dialog.Close asChild>
                        <Box paddingTop='16'>
                          <ButtonLink href={pageHref} variant='secondary'>
                            {ctaLabelWeb || ''}
                          </ButtonLink>
                        </Box>
                      </Dialog.Close>
                    </Stack>
                  )}
                </Stack>
                <Dialog.Close asChild>
                  <Box position='absolute' className='top-12 right-12'>
                    <IconButton
                      icon={<IconClose />}
                      size='small'
                      variant='tertiary'
                      aria-label={localiseString('close')}
                    />
                  </Box>
                </Dialog.Close>
              </Dialog.Content>
            </NXDPortal>
          </Dialog.Portal>
        </Dialog.Root>
      </Box>
      <Box display={{ mobile: 'block', desktop: 'none' }}>
        {ctaLabelApp && appDownloadUrlWithTrackingParams && (
          <ButtonLink
            size={size}
            variant={variant}
            href={appDownloadUrlWithTrackingParams}
            onClick={onClick}
          >
            {ctaLabelApp}
          </ButtonLink>
        )}
      </Box>
    </>
  )
}
